import type { Ref } from 'react';
import { createContext, useContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { InputProps as AriaInputProps } from 'react-aria-components/Input';
import { Input as AriaInput } from 'react-aria-components/Input';
import type { ContextValue } from 'react-aria-components/slots';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { Icon } from '@launchpad-ui/icons';

import { useLPContextProps } from './utils';

import styles from './styles/Input.module.css';

const inputStyles = cva(styles.base, {
	variants: {
		variant: {
			default: styles._default,
			minimal: styles.minimal,
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

interface InputVariants extends VariantProps<typeof inputStyles> {}
interface InputProps extends AriaInputProps, InputVariants {
	ref?: Ref<HTMLInputElement>;
}

const InputContext = createContext<ContextValue<InputProps, HTMLInputElement>>(null);
const InputValidationContext = createContext<boolean | undefined>(undefined);

/**
 * An input allows a user to input text.
 *
 * https://react-spectrum.adobe.com/react-aria/TextField.html
 */
const Input = ({ ref, ...props }: InputProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, InputContext);
	const isValid = useContext(InputValidationContext);
	const { variant = 'default' } = mergedProps;

	const input = (
		<AriaInput
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				inputStyles({ ...renderProps, variant, className }),
			)}
		/>
	);

	return isValid === undefined ? (
		input
	) : (
		<span className={styles.validInput} data-input-slot>
			{input}
			{isValid && <Icon name="check" size="small" className={styles.validIcon} data-validation-icon aria-hidden />}
		</span>
	);
};

export { Input, InputContext, InputValidationContext, inputStyles };
export type { InputProps, InputVariants };
